<template>
  <div id="landing-app" class="landing-static" :class="{ 'rtl': locale === 'ar' }">
    <WelcomeBar />
    <AppHeader
      appName="renew"
      :locale="locale"/>
    <section class="renew">
      <div class="container">
        <div class="renew__block">
          <h1 class="renew__title" >
            <slot name="header">
              <div>{{$t('renew.landing.header')}}</div>
            </slot>
          </h1>
          <form class="renew__form" @submit.prevent="openPopup">
            <div class="renew__desc">{{$t('renew_header_desc_only_mobile')}}</div>
            <div class="renew__input-block contact-form_big">
              <BaseInputPhone
                v-if="type === 'phone'"
                :default-country-code="phoneNumberInfo.countryCode"
                v-model="phoneNumberInfo.phone"
                @update="onUpdatePhone"
                :error="$v.phone.$dirty && $v.phone.$invalid"
                :valid="!$v.phone.$invalid"
                @phone-number-blur="trackPhoneInput"
              >
                <template #empty-input-error>
                  {{ $t('qic.error.fill_field') }}
                </template>
                <template #invalid-input-error>
                  {{ $t('qic.error.consist_11_digits') }}
                </template>
              </BaseInputPhone>
              <template v-else>
                <BaseInput
                  :placeholder="$t('renew_policy_number')"
                  :validation="$v.policy"
                  :mask="'#'.repeat(policyLength + 2)"
                  v-model="policy"
                  inputMode="numeric"
                  inputStyle="big"
                  @input="setPolicyNumber"
                  @blur="trackPolicyInput"
                >
                  <template v-slot:invalid-input-error>
                    <span v-if="checkPolicyNumber" class="error-check-car">{{ $t('qic.error.invalid_policy_number')}}</span>
                    <span v-else>{{ $t('qic.error.fill_field') }}</span>
                  </template>
                  <template v-slot:empty-input-error>
                    {{ $t('qic.error.fill_field') }}
                  </template>
                </BaseInput>

                <div class="renew__make-model" v-if="typeSelect === 1">
                  <p class="renew__make-model-desc">{{ $t('renew.landing.tab.policy.combined.desc')}}</p>
                  <base-select
                    style="z-index: 5;"
                    :options="makesAndModelsList"
                    :searchable="true"
                    :placeholder="$t('renew.landing.tab.policy.placeholder.make&model')"
                    class="selectBig"
                    :caret="true"
                    big
                    name="type"
                    :error="$v.carInfo.makeAndModel.$dirty && $v.carInfo.makeAndModel.$invalid"
                    select-key="modelCode"
                    select-label="name"
                    @search="getMakesAndModels"
                    :useFilteredBackend="true"
                    v-model="carInfo.makeAndModel"
                    @keydown.native="restrictInput"
                    @input="setMakesAndModels"
                  >
                    <template v-slot:empty-input-error>
                      <span v-if="checkMakeModel" class="error-check-car">{{ $t('qic.error.check_make&model') }}</span>
                      <span v-else>{{ $t('qic.error.fill_field') }}</span>
                    </template>
                  </base-select>
                  <p class="renew__make-model-link" @click="getTypeSelect">{{ $t('renew.landing.tab.policy.link.go_to_separately') }}</p>
                </div>

                <div class="renew__make-model renew__make-model--second" v-if="typeSelect === 2">
                  <p class="renew__make-model-desc">{{ $t('renew.landing.tab.policy.separately.desc') }}</p>
                  <base-select
                    style="z-index: 6;"
                    :options="makesCarList"
                    searchable
                    class="selectBig"
                    :caret="true"
                    :error="$v.carInfo.makeCode.$dirty && $v.carInfo.makeCode.$invalid"
                    big
                    name="make"
                    select-key="id"
                    select-label="name"
                    :placeholder="$t('renew.landing.tab.policy.placeholder.make')"
                    @input="setMakesList"
                    @keydown.native="restrictInput"
                    v-model="carInfo.makeCode">
                  </base-select>
                  <base-select
                    style="z-index: 5;"
                    :options="filterModelList"
                    searchable
                    class="selectBig"
                    :caret="true"
                    big
                    :error="$v.carInfo.modelCode.$dirty && $v.carInfo.modelCode.$invalid"
                    name="model"
                    select-key="id"
                    select-label="name"
                    :class="{ disabled:isModelSelectDisabled}"
                    :disabled="isModelSelectDisabled"
                    @input="setModelCode"
                    @keydown.native="restrictInput"
                    v-model="carInfo.modelCode"
                    :placeholder="$t('renew.landing.tab.policy.placeholder.model')">
                    <template v-slot:empty-input-error>
                      <span v-if="checkMakeModel" class="error-check-car">{{ $t('qic.error.check_make&model') }}</span>
                      <span v-else>{{ $t('qic.error.fill_field') }}</span>
                    </template>
                  </base-select>
                  <p class="renew__make-model-link" @click="getTypeSelect">{{ $t('renew.landing.tab.policy.link.go_to_combined') }}</p>
                </div>
              </template>
              <button class="btn" ref="proceedButton">
                <Loader v-if="isLoading"/>
                <span v-else>{{$t('renew_proceed')}}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
    <section class="bullets">
      <div class="container">
        <div class="bullets__columns">
          <div class="bullets__item">
            <div class="bullets__img"><img src="@/assets/images/easy.svg" alt="easy"></div>
            <div class="bullets__info">
              <h4>{{$t('renew_pros_easy')}}</h4>
              <p>{{$t('renew_pros_easy_desc')}}</p>
            </div>
          </div>
          <div class="bullets__item">
            <div class="bullets__img"><img src="@/assets/images/instant.svg" alt="instant"></div>
            <div class="bullets__info">
              <h4>{{$t('renew_pros_instant')}}</h4>
              <p>{{$t('renew_pros_instant_desc')}}</p>
            </div>
          </div>
          <div class="bullets__item">
            <div class="bullets__img"><img src="@/assets/images/paperless.svg" alt="paperless"></div>
            <div class="bullets__info">
              <h4>{{$t('renew_pros_paperless')}}</h4>
              <p>{{$t('renew_pros_paperless_desc')}}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="renew-help">
      <div class="container">
        <div class="renew-help__grid">
          <div class="renew-help__top">
            <h3>{{ $t('qic_need_help_popup_header') }}</h3>
            <p>{{ $t('renew_home_ready_help_desc') }}</p>
          </div>

          <div class="renew-help__bottom">
            <p>{{ $t('renew_home_reade_help_hotline') }}</p>
            <a
              href="https://api.whatsapp.com/send/?phone=97451047909&amp;text&amp;app_absent=0"
              target="_blank"
              class="btnWhatsapp btnWhatsapp_big"
              @click="trackWhatsApp"
            >
              <i class="wa"></i>
              {{ $t('qic_cant_provide_wa') }}
            </a>
          </div>
        </div>
      </div>
    </section>
    <section class="faq-cont">
      <div class="container">
        <LandingFaqs
          @showMore="trackShowMore"
          @headerClick="trackHeaderClick"
          :categoryID="faqId"/>
      </div>
    </section>
    <AppFooter
      :locale="locale"/>
    <PolicyVerificationPopup
      v-model="passport"
      @onSubmit="onSubmit"
      @close="showPopup = false"
      v-if="showPopup"/>
  </div>
</template>
<script>
import { AppHeader, AppFooter, BaseInputPhone, LandingFaqs, BaseSelect, Loader, WelcomeBar } from 'qic-components'
import PolicyVerificationPopup from '@/components/PolicyVerificationPopup.vue'
import { required } from 'vuelidate/lib/validators'
import httpClient from '../plugins/axios'
import { trackEvent, trackGtag } from '@/utils/useGtag'
import { debounce } from '@/utils/debounce'

const PHONE_TAB_ID = 'phone'
const POLICY_TAB_ID = 'policy'
const POSSIBLE_TABS = [POLICY_TAB_ID, PHONE_TAB_ID]
const filterUrlMap = {
  car: 'car',
  'boat-yacht': 'boat',
  motorcycle: 'moto',
  home: 'home'
}
export default {
  name: 'BaseLanding',
  props: [
    'filter',
    'faqId'
  ],
  components: {
    BaseSelect,
    PolicyVerificationPopup,
    AppHeader,
    BaseInputPhone,
    AppFooter,
    LandingFaqs,
    Loader,
    WelcomeBar
  },
  data () {
    return {
      phoneNumberInfo: {
        phone: null,
        formattedPhone: null,
        countryCode: 'QA',
        isValidLength: false
      },
      passport: '',
      transId: '',
      faqItems: 8,
      year: new Date().getFullYear(),
      type: PHONE_TAB_ID,
      typeSelect: 1,
      policy: null,
      policyLength: 10,
      showPopup: false,
      yourValue: null,
      byUrl: false,
      carInfo: {
        makeAndModel: null,
        makeCode: null,
        modelCode: null
      },
      carMakeAndModel: null,
      carMake: null,
      carModel: null,
      makeId: null,
      filterModelList: [],
      isModelSelectDisabled: true,
      checkMakeModel: false,
      checkPolicyNumber: false,
      isLoading: false
    }
  },
  computed: {
    locale () {
      return this.$i18n.locale
    },
    faqList () {
      return this.getFaqList()
    },
    makesAndModelsList () {
      return this.$store.state.motor.makesAndModels
    },
    makesCarList () {
      return this.$store.state.motor.makesCar
    }
  },
  methods: {
    changeType (type) {
      this.type = type
      this.trackSelection()
    },
    trackPhoneInput () {
      if (this.phoneNumberInfo.phone) {
        trackGtag('typePhoneNumber', { label: this.phoneNumberInfo.phone.length.toString() })
      }
    },
    trackSelection () {
      if (this.type === 'phone') {
        trackEvent('choosePhoneNumber')
      } else if (this.type === 'policy') {
        trackEvent('choosePolicyNumber')
      }
    },
    trackPolicyInput () {
      if (this.policy) {
        trackGtag('typePolicyNumber', { label: this.policy.length.toString() })
      }
    },
    trackWhatsApp () {
      trackEvent('ClickWhatsApp')
    },
    trackShowMore () {
      trackEvent('tapFAQmore')
    },
    trackHeaderClick (question) {
      if (question.expanded) {
        trackEvent('tapFAQ', question.text)
      }
    },
    trackError (error) {
      if (!(error instanceof Error)) {
        error = new Error(String(error))
      }
      trackGtag('input_error', { label: error.message })
    },
    setMakesAndModels (value) {
      this.$v.$reset()
      const item = this.makesAndModelsList.find((item) => item.modelCode === value)
      this.carInfo.makeCode = item.makeCode
      this.carInfo.modelCode = item.modelCode
      this.checkMakeModel = false

      const eventLabel = `${item.name}`
      const eventData = {
        label: eventLabel,
        position: this.makesAndModelsList.findIndex(item => item.name === eventLabel) + 1,
        response_amount: this.makesAndModelsList.length
      }
      trackGtag('selectModelOrBrand', eventData)
    },
    getMakesAndModels (searchValue) {
      return this.$store.dispatch('getMakesAndModels', searchValue)
    },
    async setMakesList (value) {
      this.$v.$reset()
      const item = this.makesCarList.find((item) => item.id === value)
      this.carInfo.makeCode = item.id
      this.makeId = item.id
      this.isModelSelectDisabled = true
      const modelList = await httpClient.get(`/getmodels?brand=${this.makeId}`)
        .then(({ data }) => data.models)
      this.filterModelList = modelList.sort((a, b) => b.popular - a.popular)
      this.checkMakeModel = false
      this.isModelSelectDisabled = false
      const eventLabel = `${item.name}`
      const eventData = {
        label: eventLabel,
        position: this.makesCarList.findIndex(item => item.name === eventLabel) + 1,
        response_amount: this.makesCarList.length,
        list: item.popular ? 'Popular' : 'All models'
      }
      trackGtag('selectBrand', eventData)
    },
    setModelCode (value) {
      this.$v.$reset()
      const item = this.filterModelList.find((item) => item.id === value)
      this.carInfo.modelCode = item.id
      this.checkMakeModel = false

      const eventLabel = `${item.name}`
      const eventData = {
        label: eventLabel,
        position: this.filterModelList.findIndex(item => item.name === eventLabel) + 1,
        response_amount: this.filterModelList.length,
        list: item.popular ? 'Popular' : 'All models'
      }
      trackGtag('selectModel', eventData)
    },
    setPolicyNumber (value) {
      this.$v.$reset()
      this.checkPolicyNumber = false
    },
    restrictInput (event) {
      const char = event.key
      const pattern = /[0-9a-zA-Z()/\-+ ]/
      if (!pattern.test(char)) {
        event.preventDefault()
      }
    },

    onSubmit () {
      let params = {}
      if (this.type === PHONE_TAB_ID) {
        this.$store.dispatch('setUserPhoneInfo', {
          formattedPhone: this.phoneNumberInfo.formattedPhone,
          countryCode: this.phoneNumberInfo.countryCode,
          phone: this.phoneNumberInfo.phone
        })
        params = {
          phone: this.phoneNumberInfo.phone.replace(/[\s-]/g, ''),
          qid: this.passport
        }
        trackGtag('click_proceed', {
          page: 'qid'
        })
      } else {
        this.isLoading = true
        this.$store.commit('setMakeCode', this.carInfo.makeCode)
        this.$store.commit('setModelCode', this.carInfo.modelCode)
        params = {
          policy: this.policy,
          qid: this.passport,
          transId: this.transId,
          makeCode: this.carInfo.makeCode,
          modelCode: this.carInfo.modelCode
        }
        trackGtag('click_proceed', {
          page: 'renewal_main',
          method: 'policy_id',
          value: this.policy
        })
      }
      this.$store.commit('setByUrl', this.byUrl)
      if (this.byUrl) {
        this.$router.replace({ name: 'SelectPolicy', params })
        return
      }

      if (this.type === POLICY_TAB_ID) {
        this.$store.dispatch('getActivePolicies', { policyNo: params.policy, makeCode: params.makeCode, modelCode: params.modelCode })
          .then((e) => {
            this.isLoading = false
            return this.$router.push({ name: 'SelectPolicy', params })
          })
          .catch((e) => {
            this.isLoading = false
            if (e.message === 'Invalid information') {
              this.checkMakeModel = true
              this.carInfo.makeAndModel = null
              this.carInfo.makeCode = null
              this.carInfo.modelCode = null
              this.trackError(e)
              this.isModelSelectDisabled = true
              trackGtag('input_error', {
                input_field: 'make_and_model_input',
                value: this.policy,
                text: this.$t('qic.error.check_make&model')
              })
            } else {
              this.checkPolicyNumber = true
              e.message = this.$t('qic.error.invalid_policy_number')
              trackGtag('input_error', {
                input_field: 'policy_input',
                value: this.policy,
                text: this.$t('qic.error.invalid_policy_number')
              })
              this.trackError(e)
            }
          })
      } else {
        this.$router.push({ name: 'SelectPolicy', params })
      }
    },
    changeLocale (locale) {
      if (this.locale === locale) return

      this.$router.push({ name: this.$route.name, params: { locale } })
        .then(() => {
          this.$i18n.locale = locale
        })
    },
    openPopup () {
      this.$v.$touch()

      if (this.type === PHONE_TAB_ID) {
        trackGtag('click_proceed', {
          page: 'renewal_main',
          method: 'phone',
          value: this.phoneNumberInfo.phone
        })
        if (this.$v.phone.$invalid) {
          if (!this.$v.phone.required) {
            this.trackError(this.$t('qic.error.fill_field'))
          } else {
            this.trackError(this.$t('qic.error.consist_11_digits'))
          }
        } else {
          this.$store.commit('clearUserData')
          this.$store.commit('clearMotorData')
          this.showPopup = true
        }
      } else if (this.type === POLICY_TAB_ID) {
        if (!this.$v.policy.$invalid && !this.$v.carInfo.$invalid) {
          this.$store.commit('clearUserData')
          this.$store.commit('clearMotorData')
          this.onSubmit()
        } else {
          if (this.$v.policy.$invalid) {
            trackGtag('input_error', {
              input_field: 'policy_input',
              value: this.policy,
              text: this.$t('qic.error.fill_field')
            })
          }
          if (this.$v.carInfo.$invalid) {
            trackGtag('input_error', {
              input_field: 'make_and_model_input',
              value: this.carInfo.makeAndModel,
              text: this.$t('qic.error.fill_field')
            })
          }
        }
      }
    },

    getFaqList () {
      const faqTitleTName = 'renew_faq_header'
      const faqDescTName = 'renew_faq_desc'
      const result = new Array(this.faqItems).fill({})
        .map((_, index) => ({
          question: this.$t(`${faqTitleTName}${index + 1}`),
          answer: this.$t(`${faqDescTName}${index + 1}`)
        }))
      return result
    },
    onUpdatePhone (result) {
      this.$v.$reset()
      this.phoneNumberInfo.formattedPhone = result?.formattedNumber()
      this.phoneNumberInfo.isValidLength = result?.isValidLength()
      this.phoneNumberInfo.countryCode = result?.countryCode
    },
    setPolicyFromUrlFilter () {
      const query = this.$route.query
      const isValidLength = (length) => {
        const validLengths = [this.policyLength, this.policyLength + 1, this.policyLength + 2]
        return validLengths.includes(length)
      }

      if (!Number.isNaN(+this.filter) && isValidLength(this.filter.length)) {
        this.policy = this.filter
        this.passport = query.qid || ''
        this.transId = query.transId || ''
        this.type = POLICY_TAB_ID
        this.byUrl = true
        if (
          (this.passport.length === 11 || this.passport.length === 8) &&
          !isNaN(Number(this.passport))) {
          this.onSubmit()
          return
        }
        this.$refs.proceedButton.click()
        return
      }
      if (query.policy && (query.qid || query.transId)) {
        this.policy = query.policy
        this.passport = query.qid
        this.transId = query.transId || ''
        this.type = POLICY_TAB_ID
        this.byUrl = true
        this.onSubmit()
      }
    },
    getTypeSelect () {
      this.typeSelect = this.typeSelect === 1 ? 2 : 1
      this.carInfo.makeAndModel = null
      this.carInfo.makeCode = null
      this.carInfo.modelCode = null
      trackEvent('chooseNotFindBrand')
    }
  },
  async created () {
    const type = this.$route.query.type
    this.type = type && POSSIBLE_TABS.includes(type) ? type : PHONE_TAB_ID
    await Promise.all([
      this.$store.dispatch('getMakesAndModels'),
      this.$store.dispatch('getMakesCar')
    ])
  },
  mounted () {
    this.getMakesAndModels = debounce(this.getMakesAndModels, 600)
    const queryPromocode = this.$route.query?.promo
    if (queryPromocode) {
      this.$store.commit('setMotorPromocode', queryPromocode)
      this.$store.commit('setPromocode', queryPromocode)
    }
    this.$store.commit('setRenewFilter', filterUrlMap[this.filter] || null)
    this.setPolicyFromUrlFilter()
  },
  beforeRouteEnter (to, _from, next) {
    const filter = to.params?.filter
    const nextParams = {}
    if (filter && !filterUrlMap[filter] &&
      !(!Number.isNaN(+filter) && ((filter.length === 10) || (filter.length === 12)))) {
      nextParams.name = 'NotFound'
      nextParams.params = { pathMatch: to.path.substring(1).split('/') }
      nextParams.query = to.query
      nextParams.hash = to.hash
    }
    return next(nextParams)
  },
  validations () {
    const validations = {}
    if (this.type === PHONE_TAB_ID) {
      validations.phone = {
        validPhone: () => this.phoneNumberInfo.isValidLength
      }
    }
    if (this.type === POLICY_TAB_ID) {
      validations.policy = {
        required,
        invalidInfo: () => !this.checkPolicyNumber,
        minLength: () => {
          const validLengths = [this.policyLength, this.policyLength + 1, this.policyLength + 2]
          return validLengths.includes(this.policy.length)
        }
      }
      if (this.typeSelect === 1) {
        validations.carInfo = {
          makeAndModel: {
            required,
            invalidInfo: () => !this.checkMakeModel
          }
        }
      } else {
        validations.carInfo = {
          modelCode: {
            required,
            invalidInfo: () => !this.checkMakeModel
          },
          makeCode: {
            required,
            invalidInfo: () => !this.checkMakeModel
          }
        }
      }
    }
    return validations
  }
}
</script>

<style lang="scss" scoped>
.renew{
  &__make-model{
    display: grid;
    gap: 16px;
    max-width: 416px;
    width: 100%;
    margin: 0 auto;

    &-desc{
      color: #000;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
    }

    &-link{
      color: #5927FF;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
      text-decoration-line: underline;
      cursor: pointer;
    }
  }
}

.rtl{
  .renew{
    &__form{
      direction: rtl;
    }
  }
}
</style>

<style lang="scss">
.renew {
  .error-check-car{
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }

  input.multiselect__input {
    width: 90% !important;
  }
  &__make-model{
    .error-input-message span{
      display: block;
    }

    &--second{
      .selectBig {
        &:nth-child(2){
          .error-input-message{
            display: none !important;
          }
        }
      }
    }
  }
  .disabled{
    .placeholder-container{
      pointer-events: none;
      opacity: .5;
    }
    .error {
      opacity: 1;
    }
  }
  .material-loader__circle{
    stroke: #fff;
  }

  .selectBig .error.placeholder-container label{
    top: 28px !important;
    z-index: 1;
  }
  .multiselect.multiselect--active label.multiselect__ph, .multiselect.has-value label.multiselect__ph{
    top: 14px !important;
  }
}

.rtl{
  .renew input.multiselect__input {
    text-align: right !important;
    direction: ltr !important;
  }
}
</style>
